/**
 * Host registry — barcha host adapterlari shu yerda ro'yxatga olinadi.
 *
 * `resolveActiveHost()` boot vaqtida bir marta chaqiriladi (ehtimol async,
 * chunki Telegram initData kechikishi mumkin). Aniqlangan host keyin
 * `getActiveHost()` orqali sync olinadi — `auth.api.ts`, `env.ts`,
 * `uiTokens.ts` va h.k. shu funksiyani ishlatadi.
 *
 * Yangi super app qo'shish:
 *   1. `src/hosts/<vendor>Host.ts` yarating (`defineCookieHost` factory tavsiya etiladi)
 *   2. shu fayldagi `HOSTS` ro'yxatiga qo'shing — webHost dan oldin
 *   3. `src/config/uiTokens.ts` ichida `BRANDS[<brandId>]` ga token to'plami yozing
 *   4. backend `/MiniApp/<Vendor>Login` endpointini tayyorlasin
 */

import type { HostAdapter, HostId } from "./types";
import { clickHost } from "./clickHost";
import { paymeHost } from "./paymeHost";
import { zoomradHost } from "./zoomradHost";
import { paynetHost } from "./paynetHost";
import { anorbankHost } from "./anorbankHost";
import { telegramHost } from "./telegramHost";
import { webHost } from "./webHost";

/**
 * Registry tartibi muhim:
 * 1) Domen-based hostlar birinchi (sync match)
 * 2) Runtime-based hostlar (Telegram) — initData kutish bilan
 * 3) `webHost` har doim oxirgi — catch-all
 */
const HOSTS: readonly HostAdapter[] = [
  clickHost,
  paymeHost,
  zoomradHost,
  paynetHost,
  anorbankHost,
  telegramHost,
  webHost,
];






let _active: HostAdapter | null = null;

/** Sync resolve: domain-based hostlar yoki allaqachon aniqlangan host */
function resolveSync(): HostAdapter | null {
  if (_active) return _active;
  for (const host of HOSTS) {
    if (host.match()) return host;
  }
  return null;
}

/**
 * Active host ni topadi. Domain-based hostlar darhol, Telegram esa
 * initData ulgurguncha kutadi. Bir marta aniqlangach kesh qilinadi.
 */
export async function resolveActiveHost(): Promise<HostAdapter> {
  if (_active) return _active;

  // Faza 1: sync match (Click, Zoomrad va shunga o'xshashlar — domen orqali)
  const sync = resolveSync();
  if (sync && sync.id !== "web") {
    _active = sync;
    return _active;
  }

  // Faza 2: kechikuvchi signallar (Telegram initData)
  for (const host of HOSTS) {
    if (host === webHost) continue;
    if (host.awaitMatch && (await host.awaitMatch())) {
      _active = host;
      return _active;
    }
  }

  // Faza 3: catch-all
  _active = webHost;
  return _active;
}

/**
 * Sync getter. Boot tugagandan keyin (yoki domain-based host uchun darhol)
 * ishlatish mumkin. Hech qachon aniqlanmagan paytda chaqirilsa eng yaxshi
 * sync taxminni qaytaradi (`webHost`).
 */
export function getActiveHost(): HostAdapter {
  return _active ?? resolveSync() ?? webHost;
}

/** Aktiv host ID — sync gettern, asosan komponentlarda conditional render uchun */
export function getActiveHostId(): HostId {
  return getActiveHost().id;
}

/** Aktiv host brand id — `uiTokens.ts` va theme uchun */
export function getActiveBrandId() {
  return getActiveHost().brandId;
}

// Re-exports — boshqa modullar yagona kirish nuqtasidan import qilishi uchun
export { clickHost, paymeHost, zoomradHost, paynetHost, anorbankHost, telegramHost, webHost };
export { NotTelegramEnvironmentError } from "./telegramHost";
export { UnsupportedHostError } from "./types";
export type { HostAdapter, HostId, LoginResponse, ThemeMode } from "./types";
